import ConfirmDialog from "../../modal/confirmDialog/confirmDialog";
import React, { useState, useEffect, useRef } from "react";
import ChangePasswordModal from "../../modal/changePassword/changePassword.tsx";
import { clearAuth, fetchWithAuth, setUser as setAuthUser } from "../../auth/auth";
import { getApiUrl } from "../../utils/getApiUrl";
import type { UserInfoPanelModel, UserModel } from "../../type/user.tsx";
import { useToast } from "../../components/toast/toast.tsx";
import UserAvatar from "../../components/userAvatar/userAvatar.tsx";
import Checkbox from "../../components/checkbox/checkbox.tsx";
import { getUser, isAuthenticated } from "../../auth/auth";
import { normalizeParameters } from "../../utils/usersParameters";
import ChangePackageModal from "../../modal/changePackage/changePackageModal/changePackageModal.tsx";
import type { PackageOption } from "../../type/packages.ts";
import "./userInfoPanel.scss";
import { UserPackageBadge } from "../../components/userPackageBadge/userPackageBadge.tsx";
import EditButton from "../../components/editButton/editButton.tsx";
import { useNavigate } from "react-router-dom";


const UserInfoPanel: React.FC<UserInfoPanelModel> = ({ user, onUserUpdate }) => {
  const [showChangePassword, setShowChangePassword] = useState(false);
  const handleOpenChangePassword = () => setShowChangePassword(true);
  const handleCloseChangePassword = () => setShowChangePassword(false);
  const [username, setUsername] = useState("");
  const [previousUsername, setPreviousUsername] = useState("");
  const [admin, setAdmin] = useState(false);
  const [avatar, setAvatar] = useState<string | undefined>(undefined);
  const [savingUsername, setSavingUsername] = useState(false);
  const [savingAdmin, setSavingAdmin] = useState(false);
  const [savingAvatar, setSavingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showAvatarMenu, setShowAvatarMenu] = useState(false);
  const avatarMenuRef = useRef<HTMLDivElement>(null);
  const [currentUser, setCurrentUser] = useState<UserModel | null>(null);
  const usernameInputRef = useRef<HTMLInputElement>(null);
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [showChangePackage, setShowChangePackage] = useState(false);  
  const [deletingUser, setDeletingUser] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [permDeleteChecked, setPermDeleteChecked] = useState(false);
  const nav = useNavigate();

  const { showToast } = useToast();

  useEffect(() => {
    if (user) {
      setUsername(user.username || "");
      setPreviousUsername(user.username || "");
      setAdmin(!!user.admin);
      setAvatar(user.avatar);
    }
  }, [user]);

  useEffect(() => {
    if (isAuthenticated()) {
      const userData = getUser();
      setCurrentUser(userData);
    }
  }, []);

  useEffect(() => {
    if (isEditingUsername) {
      usernameInputRef.current?.focus();
    }
  }, [isEditingUsername]);  

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (avatarMenuRef.current && !avatarMenuRef.current.contains(event.target as Node)) {
        setShowAvatarMenu(false);
      }
    };

    if (showAvatarMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAvatarMenu]);


  const handlePackageChange = async (pkg: PackageOption) => {
    try {
      await updateUserData({ idpackage: pkg.id });
      showToast(`Package changed to ${pkg.name}`, "info");
      setShowChangePackage(false);
    } catch {
      showToast("Failed to change package", "error");
    }
  };  

  // Common function to update user data
  const updateUserData = async (updates: Partial<UserModel>): Promise<UserModel> => {
    if (!user?.iduser) {
      throw new Error("User ID is required");
    }

    const response = await fetchWithAuth(getApiUrl("user/update"), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        iduser: user.iduser,
        //admin: user.admin,
        ...updates
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to update user data");
    }

    const data = await response.json();
    const dataUser = data[0] || data;

    const updatedUser: UserModel = {
      ...dataUser,
      parameters: normalizeParameters(dataUser.parameters),
    };    
    
    // Update local state
    onUserUpdate(updatedUser);
    
    // Update auth user if editing current user
    if (currentUser?.iduser === user.iduser) {
      setAuthUser(updatedUser);
    }

    return updatedUser;
  };

  const handleDeleteUser = () => {
    setShowDeleteDialog(true);
  };

  const handleConfirmDeleteUser = async (permDelete?: boolean) => {
    if (!user?.iduser) return;
    setDeletingUser(true);
    setShowDeleteDialog(false);
    try {
      const response = await fetchWithAuth(
        getApiUrl(`user/delete/${user.iduser}?permRemoval=${permDelete ? "true" : "false"}`),
        { method: "DELETE" }
      );
      if (!response.ok) {
        throw new Error("Failed to delete user");
      }
      showToast("User deleted successfully", "info");
      if (currentUser?.iduser === user.iduser) {
        clearAuth();
        nav("/login");
      }
      else {
        window.location.reload();
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      showToast("Failed to delete user. Please try again.", "error");
    } finally {
      setDeletingUser(false);
      setPermDeleteChecked(false);
    }
  };

  // Auto-save username on blur
  const handleUsernameBlur = async () => {
    if (!user || !user.iduser || username === previousUsername) return;

    setSavingUsername(true);
    try {
      await updateUserData({ username, admin: user.admin });
      setPreviousUsername(username);
    } catch (error) {
      console.error("Error updating username:", error);
      setUsername(previousUsername);
      showToast("Failed to update username. Please try again.", "error");
    } finally {
      setSavingUsername(false);
    }
  };

  // Auto-save admin toggle
  const handleAdminToggle = async () => {
    if (!user || !user.iduser) return;

    const newAdminValue = !admin;
    const previousAdminValue = admin;

    // Optimistically update UI
    setAdmin(newAdminValue);
    setSavingAdmin(true);

    try {
      await updateUserData({ admin: newAdminValue });
    } catch (error) {
      console.error("Error updating admin status:", error);
      setAdmin(previousAdminValue);
      showToast("Failed to update admin status. Please try again.", "error");
    } finally {
      setSavingAdmin(false);
    }
  };

  // Avatar upload handler with validation and auto-save
  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user || !user.iduser) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast("Please select a valid image file.", "error");
      return;
    }

    const previousAvatar = avatar;
    setShowAvatarMenu(false);
    setSavingAvatar(true);

    try {
      // Read and process image
      const img = new Image();
      const imageUrl = URL.createObjectURL(file);

      img.onload = async () => {
        URL.revokeObjectURL(imageUrl);

        const maxWidth = 200;
        const maxHeight = 200;
        
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions maintaining aspect ratio
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        // Create canvas and resize/draw image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          throw new Error("Failed to get canvas context");
        }

        // Draw resized image
        ctx.drawImage(img, 0, 0, width, height);
        const base64Avatar = canvas.toDataURL(file.type);

        try {
          await updateUserData({ admin: user.admin, avatar: base64Avatar });
          setAvatar(base64Avatar);
          showToast("Avatar updated successfully!", "info");
        } catch (error) {
          console.error("Error uploading avatar:", error);
          setAvatar(previousAvatar);
          showToast("Failed to upload avatar. Please try again.", "error");
        } finally {
          setSavingAvatar(false);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        showToast("Failed to load image. Please try another file.", "error");
        setSavingAvatar(false);
      };

      img.src = imageUrl;

    } catch (error) {
      console.error("Error uploading avatar:", error);
      setAvatar(previousAvatar);
      showToast("Failed to upload avatar. Please try again.", "error");
      setSavingAvatar(false);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove avatar with auto-save
  const handleAvatarRemove = async () => {
    if (!user) return;

    const previousAvatar = avatar;
    setAvatar(undefined);
    setShowAvatarMenu(false);
    setSavingAvatar(true);

    try {
      await updateUserData({ admin: user.admin, avatar: "" });
      showToast("Avatar removed successfully!", "info");
    } catch (error) {
      console.error("Error removing avatar:", error);
      setAvatar(previousAvatar);
      showToast("Failed to remove avatar. Please try again.", "error");
    } finally {
      setSavingAvatar(false);
    }
  };

  const handleAddOrChangePhoto = () => {
    fileInputRef.current?.click();
    setShowAvatarMenu(false);
  };

  const toggleAvatarMenu = () => {
    setShowAvatarMenu(!showAvatarMenu);
  };

  return (
    <div className="user-info-panel">
      <h3 className="d-flex align-items-center gap-2">
        <i className="bi bi-person-circle"></i>

        {!isEditingUsername ? (
          <>
            <span>{user?.username || "User Info"}</span>

            <EditButton
              className="btn p-0 ms-2"
              onClick={() => setIsEditingUsername(true)}
              title="Edit username"
              disabled={savingUsername}
            />
          </>
        ) : (
          <input
            ref={usernameInputRef}
            type="text"
            value={username}
            maxLength={20}
            onChange={e => setUsername(e.target.value)}
            onBlur={async () => {
              await handleUsernameBlur();
              setIsEditingUsername(false);
            }}
            className="form-control form-control-sm"
            style={{ maxWidth: 240 }}
            disabled={savingUsername}
          />
        )}
      </h3>
      
      <h5>
        {user?.email}
      </h5>

      <div className="user-info-panel-package">
        <div className="user-info-panel-package-content">
          <UserPackageBadge packageName={user?.package_name} />
          {currentUser?.admin && (
          <EditButton
            className="btn p-0 ms-2"
            onClick={() => setShowChangePackage(true)}
            title="Change package"
          />
          )}
        </div>
      </div>

      <div className="user-update-avatar">
        <div className="avatar-wrapper" ref={avatarMenuRef}>
          <UserAvatar avatar={avatar} username={username} classname="user-avatar-img" />
          <button
            type="button"
            className="avatar-camera-btn"
            onClick={toggleAvatarMenu}
            aria-label="Avatar options"
            disabled={savingAvatar}
          >
            {savingAvatar ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (
              <i className="bi bi-camera-fill"></i>
            )}
          </button>

          {/* Dropdown menu */}
          {showAvatarMenu && (
            <div className="avatar-menu">
              <button
                type="button"
                className="avatar-menu-item"
                onClick={handleAddOrChangePhoto}
              >
                {avatar ? "Zmień zdjęcie" : "Dodaj zdjęcie"}
              </button>
              {avatar && (
                <button
                  type="button"
                  className="avatar-menu-item delete"
                  onClick={handleAvatarRemove}
                >
                  Usuń zdjęcie
                </button>
              )}
            </div>
          )}
        </div>
        <input
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleAvatarChange}
        />
      </div>

      <div className="user-update-fields">
        {user?.login_type !== 2 && (
          <button
            className="btn btn-success"
            type="button"
            onClick={handleOpenChangePassword}
            disabled={!user?.iduser}
          >
            Change Password
          </button>
        )}
            <button
              className="btn btn-danger"
              type="button"
              onClick={handleDeleteUser}
              disabled={!user?.iduser || deletingUser}
            >
              {deletingUser ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Deleting...
                </>
              ) : (
                "Delete Account"
              )}
            </button>
            <ConfirmDialog
              isOpen={showDeleteDialog}
              title="Delete Account"
              message={`Are you sure you want to delete user "${user?.username ?? ""}"? This action cannot be undone.`}
              onConfirm={handleConfirmDeleteUser}
              onCancel={() => { setShowDeleteDialog(false); setPermDeleteChecked(false); }}
              showPermanentDeleteCheckbox={true}
              permanentDeleteChecked={permDeleteChecked}
              onPermanentDeleteChange={setPermDeleteChecked}
              permanentDeleteInfo={
                "Permanent deletion will erase all your account data, including dictionaries, words, and personal settings. This action is irreversible."
              }
            />
      </div>

      {user?.admin && (
      <Checkbox
        label="Admin"
        description="User administrator"
        checked={admin}
        onChange={handleAdminToggle}
        saving={savingAdmin}
        variant="compact"
      />
      )}

      {/* Password change modal for this user */}
      <ChangePasswordModal
        show={showChangePassword}
        onClose={handleCloseChangePassword}
        iduser={user?.iduser ?? 0}
        isAdmin={currentUser?.admin ?? false}
      />

      {/* Package change modal */}
      <ChangePackageModal
        show={showChangePackage}
        onClose={() => setShowChangePackage(false)}
        currentPackageId={user?.idpackage}
        onConfirm={handlePackageChange}
      />      
    </div>


    
  );
};

export default UserInfoPanel;